import { O, useLocalStorageActions } from '../../../features/ui'
import { DynamoApi } from '../../../services/dynamo'
import { useStateHooks } from '../../../state'
import { RangeParams, Params } from '../../../state/reducers/types'
import { ArchivableDataType, DataType, TableName } from '../../../tables'
import { dedupe } from '../../../utils/listUtils'
import { useSystemStateHooks } from '../../system'
import { useFetchActions } from '../actions/fetchActions'
import { QueryOptions } from '../../../services/dynamo'
import { ListResponse } from '../types'

export const useLoaders = (useCompanyId = true) => {
    const systemHooks = useSystemStateHooks()
    const { getList, find, findByIds, findMultiple, findFiltered } = useStateHooks()

    const fetchActions = useFetchActions()
    const { getLoadedTablesData, saveLoadedTablesData } = useLocalStorageActions()

    return (
        companyId = useCompanyId ? systemHooks.companyId : undefined,
        dynamoApi?: DynamoApi
    ) => {
        const loadMultiple = async (
            table: TableName,
            options?: QueryOptions,
            optimized?: boolean // TODO deberia ser por default optimized y cambiar este param a dbRequest
        ): Promise<ListResponse<DataType>> => {
            let list = findMultiple(table, options)
            
            if (!optimized || list.length === 0) {
                const { fetchMultiple } = fetchActions(companyId, dynamoApi)
                return fetchMultiple(table, options)
            }
            
            return {
                dataTypes: list
            }
        }

        const count2 = async (
            table: TableName,
            options?: QueryOptions
        ) => {
            const { count2 } = fetchActions(companyId, dynamoApi)
            return count2(table, options)
        }
        
        // TODO analizar los de abajo de esta linea 1 por 1 si se puede reemplazar por loadMultiple
        // los que no, implementar con mismo criterio que loadMultiple

        const loadAll = async (
            table: TableName,
            dbRequest = false,
            active = true
        ) => {
            let list = getList(table)
            if (dbRequest || !hasLoaded(table) || list.length === 0) {
                const { fetchAll } = fetchActions(companyId, dynamoApi)
                list = await fetchAll(table)
                addToLoaded(table)
            }
            return active ? list.filter(item => !item.hasOwnProperty('archived') || !(item as ArchivableDataType).archived) : list
        }

        const loadBatch = async (
            table: TableName,
            startKey?: Params,
            limit?: number
        ) => {
            const { fetchBatch } = fetchActions(companyId, dynamoApi)
            return fetchBatch(table, startKey, limit)
        }

        const load = async (
            table: TableName,
            id: string,
            dbRequest = false
        ): Promise<O<DataType>> => {
            let dataType = find(table, id)
            if (dbRequest || !dataType) {
                const { fetch } = fetchActions(companyId, dynamoApi)
                dataType = await fetch(table, id)
            }
            return dataType
        }

        const loadByIds = async (
            table: TableName,
            ids: string[],
            dbRequest = false
        ): Promise<DataType[]> => {
            if (ids.length === 0) {
                return []
            }
            const dedupedIds = dedupe(ids)
            let list = findByIds(table, dedupedIds)
            if (dbRequest || list.length < dedupedIds.length) {
                const { fetchByIds } = fetchActions(companyId, dynamoApi)
                list = await fetchByIds(table, dedupedIds)
            }
            return list
        }

        const loadFiltered = async (
            table: TableName,
            dbRequest = false,
            params?: Params,
            ranges?: RangeParams,
            existingParams?: string[]
        ): Promise<DataType[]> => {
            let list = findFiltered(table, params, ranges, existingParams)
            if (dbRequest || list.length === 0) {
                const { fetchFiltered } = fetchActions(companyId, dynamoApi)
                list = await fetchFiltered(table, params, ranges, existingParams)
            }
            return list
        }

        const loadFilteredByList = async (
            table: TableName,
            dbRequest = false,
            key: string,
            values: string[]
        ): Promise<DataType[]> => {
            let list = findFiltered(table, { [key]: values })
            if (dbRequest || list.length === 0) {
                const { fetchFilteredByList } = fetchActions(companyId, dynamoApi)
                list = await fetchFilteredByList(table, key, values)
            }
            return list
        }

        const count = (
            table: TableName,
            startKey?: Params,
            limit?: number,
            params?: Params
        ): Promise<number> => {
            const { count } = fetchActions(companyId, dynamoApi)
            return count(table, startKey, limit, params)
        }
        
        const hasLoaded = (table: string) => !!getLoadedTablesData().find(loadedTable => loadedTable === table)

        const addToLoaded = (table: string) => {
            const loadedTables = getLoadedTablesData()
            if (!loadedTables.find(loadedTable => loadedTable === table)) {
                loadedTables.push(table)
                saveLoadedTablesData(loadedTables)
            }
        }

        return {
            loadMultiple,
            count2,
            
            loadAll,
            loadBatch,
            load,
            loadByIds,
            loadFiltered,
            loadFilteredByList,
            count,
            hasLoaded,
            addToLoaded
        }
    }
}
